import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/auth';

/**
 * A 4xx is the server's answer, not an outage: asking again gets the same
 * answer, only later (fan QA M4 — a not-started match's box score spun ~7 s on
 * three backed-off retries of a 404 before redirecting). Only a network failure
 * or a 5xx is worth retrying, and at most twice.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status < 500) return false;
  return failureCount < 2;
}

/**
 * How long fetched data counts as fresh. react-query's default is 0, which made
 * EVERY screen mount refetch everything it shows — opening a match, going back
 * and opening it again was two full round trips, and so was every tab-to-detail
 * hop. 30 s keeps data that changes on a human timescale from being re-requested
 * on each navigation while anything a mutation touches still refetches at once
 * (those paths call invalidateQueries, which ignores staleTime). Queries that
 * must be live (the seat map) opt out with staleTime: 0 where they are defined.
 */
export const DEFAULT_STALE_MS = 30_000;
/** Keep unused results this long so going back to a screen paints from cache
 *  (stale-while-revalidate) instead of from a spinner. react-query's default is 5 min. */
export const DEFAULT_GC_MS = 10 * 60_000;

/**
 * App-wide react-query client. Lives outside Providers so non-component code
 * (e.g. setAppLanguage in @/i18n) can invalidate queries — server responses are
 * localized, so cached data belongs to the language it was fetched in.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      staleTime: DEFAULT_STALE_MS,
      gcTime: DEFAULT_GC_MS,
      // Web only (the webview): a tab regaining focus refetched every active
      // query. Native has no window focus, so this is a no-op there.
      refetchOnWindowFocus: false,
    },
  },
});
